import { cliSimpleGit } from '../cli-simple-git.js';
import { yieldToEventLoop } from '../../runtime/yield-to-event-loop.js';
import { LIST_DIR_YIELD_EVERY, type ListEntry } from '../../files/list-dir/types.js';
import { sortListEntries } from '../../files/list-dir/sort-entries.js';
import { resolveBranchCommit } from './resolve-branch-commit.js';

function parseLsTreeLine(line: string): { mode: string; objectType: string; name: string } | null {
  const tab = line.indexOf('\t');
  if (tab < 0) return null;
  const meta = line.slice(0, tab).trim().split(/\s+/);
  if (meta.length < 3) return null;
  const mode = meta[0]!;
  const objectType = meta[1]!;
  let name = line.slice(tab + 1);
  if (name.startsWith('"') && name.endsWith('"')) {
    name = name
      .slice(1, -1)
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\');
  }
  return { mode, objectType, name };
}

function childPath(relativePath: string, name: string): string {
  const base = relativePath.replace(/^\/+/, '').replace(/\\/g, '/');
  if (!base || base === '.') return name;
  return `${base}/${name}`.replace(/\\/g, '/');
}

function lsTreeArgs(commit: string, relativePath: string): string[] {
  const rel = relativePath.replace(/^\/+/, '').replace(/\\/g, '/');
  if (!rel || rel === '.') return ['ls-tree', commit];
  // `commit -- path` lists the path object itself (one self-named tree row). `commit:path` lists directory contents.
  return ['ls-tree', `${commit}:${rel}`];
}

/**
 * Lists one directory level from a branch ref via `git ls-tree` (repo-relative paths).
 */
export async function listGitTreeDirAsync(
  repoAbsPath: string,
  branch: string,
  relativePath: string,
): Promise<{ entries: ListEntry[] } | { error: string }> {
  await yieldToEventLoop();
  const commit = await resolveBranchCommit(repoAbsPath, branch);
  if (!commit) return { error: 'Branch not found' };
  try {
    const raw = await cliSimpleGit(repoAbsPath).raw(lsTreeArgs(commit, relativePath));
    await yieldToEventLoop();
    const lines = String(raw ?? '')
      .split(/\r?\n/)
      .filter((line) => line.trim().length > 0);
    const entries: ListEntry[] = [];
    for (let i = 0; i < lines.length; i++) {
      if (i > 0 && i % LIST_DIR_YIELD_EVERY === 0) {
        await yieldToEventLoop();
      }
      const parsed = parseLsTreeLine(lines[i]!);
      if (!parsed) continue;
      const { mode, objectType, name } = parsed;
      if (!name || name === '.' || name === '..') continue;
      const isSymlink = mode === '120000';
      const isDir = objectType === 'tree';
      entries.push({
        name,
        path: childPath(relativePath, name),
        isDir,
        ...(isSymlink ? { isSymlink: true } : {}),
      });
    }
    if (entries.length >= LIST_DIR_YIELD_EVERY) {
      await yieldToEventLoop();
    }
    return { entries: sortListEntries(entries) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    if (/not a tree object|does not exist|bad revision/i.test(message)) {
      return { error: 'Path not found in branch' };
    }
    return { error: message };
  }
}
